import { CSSProperties, useEffect, useRef } from "react";
import { useStore } from "reactflow";
import { HelperLineSegment } from "../../utils/helperLines"; // Import the new type

const canvasStyle: CSSProperties = {
  width: "100%",
  height: "100%",
  position: "absolute",
  zIndex: 10,
  pointerEvents: "none",
};

export type HelperLinesProps = {
  horizontal: number | null;
  vertical: number | null;
  groupBounds?: { x: number; y: number; width: number; height: number } | null;
  // Add a new prop for fixed and spacing lines
  fixedLines?: HelperLineSegment[];
  spacingLines?: HelperLineSegment[];
};

/**
 * A component to display helper lines for node alignment and distribution.
 * It puts a canvas on top of the React Flow pane and draws the lines using the canvas API.
 */
function HelperLinesRenderer({ horizontal, vertical, groupBounds, fixedLines = [], spacingLines = [] }: HelperLinesProps) {
  const width = useStore(state => state.width);
  const height = useStore(state => state.height);
  const tX = useStore(state => state.transform[0]);
  const tY = useStore(state => state.transform[1]);
  const tScale = useStore(state => state.transform[2]);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (!ctx || !canvas) {
      return;
    }

    // Clear canvas if no helper lines are present
    if (horizontal === null && vertical === null && fixedLines.length === 0 && spacingLines.length === 0) {
        ctx.clearRect(0, 0, width, height);
        return;
    }

    // Set canvas dimensions with device pixel ratio for crisp lines
    const dpi = window.devicePixelRatio || 1;
    canvas.width = width * dpi;
    canvas.height = height * dpi;
    ctx.scale(dpi, dpi);

    // Clear the entire canvas
    ctx.clearRect(0, 0, width, height);

    // Apply flow transform
    ctx.save();
    ctx.translate(tX, tY);
    ctx.scale(tScale, tScale);

    // Define styles for different line types
    const styles = {
      align: {
        strokeStyle: "#0066ff", // Blue for alignment
        lineWidth: 1 / tScale, // Thinner line for better precision
        lineDash: [5 / tScale, 3 / tScale],
        opacity: 1.0,
      },
      spacing: {
        strokeStyle: "#ff6600", // Orange for spacing
        lineWidth: 2 / tScale,
        lineDash: [], // No dash for spacing
        opacity: 0.5, // Increased opacity for better visibility
      },
      fixed: {
        strokeStyle: "#52c41a", // Green for fixed distance (e.g., 16px)
        lineWidth: 2 / tScale,
        lineDash: [], // No dash for fixed distance
        opacity: 0.5, // Increased opacity for better visibility
      },
    };

    // Draw horizontal helper line
    if (horizontal !== null) {
      // Set style for alignment lines
      ctx.strokeStyle = styles.align.strokeStyle;
      ctx.lineWidth = styles.align.lineWidth;
      ctx.setLineDash(styles.align.lineDash);
      ctx.globalAlpha = styles.align.opacity;
      
      // Calculate line bounds
      const y = Math.round(horizontal) + 0.5; // Align to pixel grid for crisp lines
      let x1 = 0;
      let x2 = width / tScale;

      // If we have groupBounds, adjust the line to stay within the group
      if (groupBounds) {
        x1 = Math.max(x1, groupBounds.x);
        x2 = Math.min(x2, groupBounds.x + groupBounds.width);
      }

      // Draw the line
      ctx.beginPath();
      ctx.moveTo(x1, y);
      ctx.lineTo(x2, y);
      ctx.stroke();
    }

    // Draw vertical helper line
    if (vertical !== null) {
      // Set style for alignment lines
      ctx.strokeStyle = styles.align.strokeStyle;
      ctx.lineWidth = styles.align.lineWidth;
      ctx.setLineDash(styles.align.lineDash);
      ctx.globalAlpha = styles.align.opacity;
      
      // Calculate line bounds
      const x = Math.round(vertical) + 0.5; // Align to pixel grid for crisp lines
      let y1 = 0;
      let y2 = height / tScale;

      // If we have groupBounds, adjust the line to stay within the group
      if (groupBounds) {
        y1 = Math.max(y1, groupBounds.y);
        y2 = Math.min(y2, groupBounds.y + groupBounds.height);
      }

      // Draw the line
      ctx.beginPath();
      ctx.moveTo(x, y1);
      ctx.lineTo(x, y2);
      ctx.stroke();
    }
    
    // Draw fixed distance lines
    if (fixedLines.length > 0) {
      ctx.strokeStyle = styles.fixed.strokeStyle;
      ctx.lineWidth = styles.fixed.lineWidth;
      ctx.setLineDash(styles.fixed.lineDash);
      ctx.globalAlpha = styles.fixed.opacity;
      
      fixedLines.forEach(line => {
        // Draw the line
        ctx.beginPath();
        ctx.moveTo(line.x1, line.y1);
        ctx.lineTo(line.x2, line.y2);
        ctx.stroke();
      });
    }
    
    // Draw spacing lines
    if (spacingLines.length > 0) {
      ctx.strokeStyle = styles.spacing.strokeStyle;
      ctx.lineWidth = styles.spacing.lineWidth;
      ctx.setLineDash(styles.spacing.lineDash);
      ctx.globalAlpha = styles.spacing.opacity;
      
      spacingLines.forEach(line => {
        // Draw the line
        ctx.beginPath();
        ctx.moveTo(line.x1, line.y1);
        ctx.lineTo(line.x2, line.y2);
        ctx.stroke();
      });
    }

    // Restore canvas context
    ctx.globalAlpha = 1.0; // Reset opacity
    ctx.restore(); 

  }, [width, height, tX, tY, tScale, horizontal, vertical, groupBounds, fixedLines, spacingLines]);

  return (
    <canvas
      ref={canvasRef}
      className="react-flow__canvas"
      style={canvasStyle}
    />
  );
}

export default HelperLinesRenderer;
